import { FC, useCallback, useEffect, useState } from 'react'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Layout } from '@/apps/latest/components/templates/Layout'
import { NutritionKarteCurrent } from '@/apps/latest/features/nutrition/NutritionKarteCurrent'
import { NutritionMaterialsBeforeRound } from '@/apps/latest/features/nutrition/NutritionMaterials'
import { NutritionRecordingBar } from '@/apps/latest/features/nutrition/NutritionRecordingBar'
import { NutritionRecordRounds } from '@/apps/latest/features/nutrition/NutritionRecordRounds'
import { NutritionStaffMenu } from '@/apps/latest/features/nutrition/NutritionStaffMenu'
import { renderNutritionSidebar } from '@/apps/latest/features/nutrition/nutritionShell'
import {
  useNutritionCustomer,
  useNutritionRounds,
  useStartRound,
  type RoundStartedBy,
} from '@/apps/latest/features/nutrition/useNutritionCustomers'
import { ActiveRecordingProvider } from '@/apps/latest/shared/contexts/ActiveRecordingContext'

// お客様のカルテ (/nutrition/customers/:customerId)。URL はこの 1 つで、今回の有無で替えない (design-book ADR-0086 決定 5)。
// 今回は back が回の一覧の印 (current) で返す (いちばん新しい回が、まだ着地していないか今日始めたものなら、その回。中身の無い器は除く)。
// 今回が無ければ、名前の行・添付資料とメモ・前の回を今回のある形と同じ並びで出し、● / 資料を足す / メモを書く の
// 最初の 1 手で今回を始めて、同じ面のまま今回の段に入る。開いて見るだけでは何も作らない (ADR-0084)。
// 登録の 1 手で始めた今回 (/nutrition/new から)。カルテはその記録で録音を始める
export interface KarteState {
  started: string
}

const NutritionKartePage: FC = () => {
  const { customerId = '' } = useParams<{ customerId: string }>()
  return (
    <ActiveRecordingProvider>
      <Karte
        // お客様を替えたら、始めた手と今回を持ち越さない
        key={`nutritionKarte-${customerId}`}
        customerId={customerId}
      />
    </ActiveRecordingProvider>
  )
}

const Karte = ({ customerId }: { customerId: string }) => {
  const customer = useNutritionCustomer(customerId)
  const rounds = useNutritionRounds(customerId)
  const start = useStartRound(customerId)
  const location = useLocation()
  const navigate = useNavigate()
  // 最初の 1 手で始めた今回。録っている間の記録は中身の無い器 (batch は止めるまで送らない) で回の一覧に来ないので、
  // 始めた id を手元で持つ。back の今回の印が来たらそちらを読む。登録で始めた今回は URL の state で来る
  const fromRegister = (location.state as KarteState | null)?.started ?? null
  const [started, setStarted] = useState<string | null>(fromRegister)
  const [startedBy, setStartedBy] = useState<RoundStartedBy | null>(
    fromRegister ? 'record' : null
  )
  // 受け取った state は履歴から外す (再読み込みや戻るで、録音をもう一度始めない)
  useEffect(() => {
    if (fromRegister)
      navigate(location.pathname, { replace: true, state: null })
  }, [fromRegister, location.pathname, navigate])
  const [starting, setStarting] = useState(false)
  const currentId = rounds.data?.find((r) => r.current)?.id ?? started

  const begin = (eventId: string, by: RoundStartedBy | null) => {
    setStartedBy(by)
    setStarted(eventId)
  }
  const record = () => {
    setStarting(true)
    start()
      .then((eventId) => begin(eventId, 'record'))
      .catch(() => undefined)
      .finally(() => setStarting(false))
  }
  const onStartedByUsed = useCallback(() => setStartedBy(null), [])
  const { refetch } = rounds
  const onGone = useCallback(() => {
    setStarted(null)
    void refetch()
  }, [refetch])

  if (currentId)
    return (
      <NutritionKarteCurrent
        key={`nutritionRound-${currentId}`}
        eventId={currentId}
        customerId={customerId}
        startedBy={startedBy}
        onStartedByUsed={onStartedByUsed}
        onGone={onGone}
      />
    )

  return (
    <Layout
      breadcrumb={['お客様', customer.data?.name ?? '']}
      renderSidebar={renderNutritionSidebar}
      // 前の回の格子は 720px だと欄が縦に割れる (今回のある形と同じ)
      wide={(rounds.data ?? []).length > 0}
    >
      {customer.isError ? (
        <Typography variant="body2" color="text.secondary">
          お客様が見つかりません。
        </Typography>
      ) : !customer.data || !rounds.data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Stack spacing={3}>
          <NutritionRecordingBar
            customer={customer.data}
            recording={{
              status: 'idle',
              stream: null,
              elapsedSeconds: 0,
              onStart: record,
              onStop: () => undefined,
              busy: starting,
            }}
            pillSlot={<NutritionStaffMenu />}
          />
          <NutritionMaterialsBeforeRound
            start={start}
            onStarted={(eventId, by) =>
              begin(eventId, by === 'memo' ? 'memo' : null)
            }
          />
          <NutritionRecordRounds customerId={customerId} />
        </Stack>
      )}
    </Layout>
  )
}

export default NutritionKartePage
